import React, { useEffect, useState } from "react";
import { X, Loader2, History, UserPen, Send, Mail } from "lucide-react";
import { reportsApi, formatAuDate } from "../lib/events";
import { extractError } from "../lib/api";

const ROLE_LABEL = {
  platform_admin: "Platform Admin",
  event_owner: "Event Owner",
  team_member: "Team member",
};

export function TicketHistoryModal({ eventId, ticket, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await reportsApi.ticketHistory(eventId, ticket.id || ticket.ticket_id);
        if (alive) setItems(res.items || []);
      } catch (e) {
        if (alive) setError(extractError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [eventId, ticket]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60" data-testid="ticket-history-modal">
      <div className="bg-[var(--gt-bg)] border-2 border-[var(--gt-ink)] w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b-2 border-[var(--gt-ink)] px-5 py-3">
          <div className="flex items-center gap-2">
            <History size={16} />
            <h3 className="font-heading text-lg font-black tracking-tight">Ticket history</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="ticket-history-close" className="p-1 hover:opacity-70">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4">
          <div className="text-xs text-[var(--gt-ink-3)] mb-4">
            {ticket.ticket_type_name ? `${ticket.ticket_type_name} · ` : ""}{ticket.code}
          </div>

          {loading ? (
            <div className="py-8 flex items-center gap-2 text-sm text-[var(--gt-ink-2)]">
              <Loader2 size={16} className="animate-spin" /> Loading history…
            </div>
          ) : error ? (
            <div className="py-6 text-sm text-[var(--gt-accent)]" data-testid="ticket-history-error">{error}</div>
          ) : items.length === 0 ? (
            <div className="py-8 text-sm text-[var(--gt-ink-3)] text-center" data-testid="ticket-history-empty">
              No edits or resends recorded for this ticket.
            </div>
          ) : (
            <ol className="space-y-4">
              {items.map((it, i) => (
                <li key={i} className="flex gap-3" data-testid={`ticket-history-item-${i}`}>
                  <div className="mt-0.5 shrink-0">
                    {it.action === "ticket_resent"
                      ? <Send size={15} className="text-[var(--gt-ink-2)]" />
                      : <UserPen size={15} className="text-[var(--gt-ink-2)]" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-widest text-[var(--gt-ink-3)]">
                      {formatAuDate(it.timestamp)}
                    </div>
                    <div className="font-semibold text-sm">{it.action_label}</div>
                    {it.action === "ticket_attendee_edited" && it.fields_changed?.length > 0 && (
                      <div className="text-xs text-[var(--gt-ink-2)] mt-0.5">
                        Changed: {it.fields_changed.join(", ")}
                        {it.email_resent && (
                          <span className="inline-flex items-center gap-1 ml-2 text-[var(--gt-ink-3)]">
                            <Mail size={11} /> ticket re-sent
                          </span>
                        )}
                      </div>
                    )}
                    <div className="text-xs text-[var(--gt-ink-3)] mt-0.5">
                      By {it.actor_name || "Unknown"}{it.actor_role ? ` (${ROLE_LABEL[it.actor_role] || it.actor_role})` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

export default TicketHistoryModal;
